// GraphQL operations for the Projects domain (list + create).
//
// Each frontend feature owns its own operations file so screens added in later
// tasks (Project detail, Course detail, profiles, generations) can define their
// documents alongside this one without touching a shared monolith. This file is
// the single place the Projects list / create-project screens source their
// `myProjects` query and `createProject` mutation from.
//
// Requirements 2.1, 2.2, 2.3, 2.4. Design → GraphQL Schema (Query.myProjects,
// Mutation.createProject) and Frontend (Projects list).

import { gql } from '@apollo/client';
import type { Project, ProjectMember } from '@courseai/domain';

/**
 * The subset of a Project the Projects list renders: identity plus the name,
 * description, and the members needed to show the current user's role. The
 * server returns the caller's own membership among `members` (Req 2.3).
 */
export interface ProjectListItem extends Pick<
  Project,
  'projectId' | 'ownerUserId' | 'name' | 'description'
> {
  readonly members: ReadonlyArray<Pick<ProjectMember, 'userId' | 'role'>>;
}

/** Shape of the `myProjects` query result. */
export interface MyProjectsData {
  readonly myProjects: ReadonlyArray<ProjectListItem>;
}

/**
 * List the projects the authenticated creator is a member of. The API derives
 * the creator identity from the JWT, so no identity is sent in the request
 * (Req 2.3, 2.4). `members` is requested so the UI can resolve the caller's
 * role client-side.
 */
export const MY_PROJECTS = gql`
  query MyProjects {
    myProjects {
      projectId
      ownerUserId
      name
      description
      members {
        userId
        role
      }
    }
  }
`;

/** Variables for the `createProject` mutation. */
export interface CreateProjectVariables {
  readonly input: {
    readonly name: string;
    readonly description?: string;
  };
}

/** Shape of the `createProject` mutation result. */
export interface CreateProjectData {
  readonly createProject: ProjectListItem;
}

/**
 * Create a project owned by the authenticated creator. Server-side the caller
 * is recorded as the owner member and the name is validated to 1–100 chars
 * (Req 2.1, 2.2); the client validates the name too for immediate feedback.
 */
export const CREATE_PROJECT = gql`
  mutation CreateProject($input: CreateProjectInput!) {
    createProject(input: $input) {
      projectId
      ownerUserId
      name
      description
      members {
        userId
        role
      }
    }
  }
`;
